import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    AttachmentHandler,
    CurrentPersonDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    fromLocalDate,
    Organisation,
    PersonRolesDto,
    PersonService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { personSearch } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { LinkAction } from 'libs/attachments/src/lib/link-manager/link-manager.interface';
import { AttachmentHandlerService } from 'libs/comments/src/lib/attachment-handler.service';
import {
    Comment,
    CommentDelete,
    CommentUpdate
} from 'libs/comments/src/lib/interfaces';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import { GeneralTask } from 'libs/flyfreely/src/lib/services/generalTasks.service';
import {
    formatGeneralTasksPriority,
    formatGeneralTasksStatuses,
    GeneralTasksDataService
} from 'libs/outstanding-tasks/src/lib/widgets/general-task/general-tasks-data.service';
import { Delta } from 'quill';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'general-task-edit',
    templateUrl: './general-task-edit.component.html',
    styleUrls: ['./general-task-edit.component.css']
})
export class GeneralTaskEditComponent implements OnInit, OnDestroy {
    @Input() generalTaskId: number;
    @Output() editingCancelled = new EventEmitter<void>();
    @Output() taskUpdated = new EventEmitter<GeneralTask>();

    generalTask: GeneralTask;
    organisation: Organisation;
    comments: Comment[];
    currentUser: CurrentPersonDto | undefined;
    attachmentsHandler: AttachmentHandler;
    editGeneralTaskForm: FormGroup;

    placeholderText =
        'Add a comment. You can tag people with "@", RPA with "#RPA", and batteries with "#BATTERY"';

    personList$: Observable<PersonRolesDto[]>;
    priorityOptions = Object.entries(formatGeneralTasksPriority).map(
        ([value, label]) => ({ label, value })
    );
    statusOptions = Object.entries(formatGeneralTasksStatuses).map(
        ([value, label]) => ({ label, value })
    );

    protected readonly LinkAction = LinkAction;
    protected readonly personSearch = personSearch;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private personService: PersonService,
        private generalTasksDataService: GeneralTasksDataService,
        private workspaceStateService: WorkspaceStateService,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService,
        private attachmentHandlerService: AttachmentHandlerService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit(): void {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.personList$ = this.personService.findPersonnel(
                    this.organisation.id
                );
                this.refreshData();
                this.attachmentsHandler =
                    this.generalTasksDataService.attachmentHandler(
                        this.generalTaskId,
                        this.organisation.id
                    );
                this.attachmentHandlerService.attachmentHandler =
                    this.attachmentsHandler;
                this.generalTasksDataService.commentChange$
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(() => {
                        this.refreshComments();
                    });
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshData() {
        this.generalTasksDataService
            .findTaskById(this.generalTaskId)
            .subscribe({
                next: result => {
                    this.generalTask = result;
                    this.initEditForm();
                    this.refreshComments();
                }
            })
            .add(this.workTracker.createTracker());
    }

    initEditForm(): void {
        this.editGeneralTaskForm = new FormGroup({
            summary: new FormControl(
                this.generalTask?.summary,
                Validators.required
            ),
            description: new FormControl(
                this.generalTask?.description,
                Validators.required
            ),
            dueDate: new FormControl(
                this.generalTask.dueDate
                    ? fromLocalDate(this.generalTask.dueDate)
                    : null
            ),
            priority: new FormControl(
                this.generalTask?.priority,
                Validators.required
            ),
            assignee: new FormControl(this.generalTask?.assignee?.id),
            status: new FormControl(
                this.generalTask.status,
                Validators.required
            )
        });
    }

    onCancel() {
        if (this.editGeneralTaskForm?.dirty) {
            this.confirmCancel();
        } else {
            this.cancelEdit();
        }
    }

    confirmCancel() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Confirm Cancel',
                `You have unsaved changes, are you sure you want to cancel?`,
                'Yes',
                () => Promise.resolve()
            )
            .then(() => this.cancelEdit());
    }

    cancelEdit() {
        this.editingCancelled.emit();
    }

    onSave() {
        const value = this.editGeneralTaskForm.value;
        this.generalTasksDataService
            .updateTask({
                taskId: this.generalTask.id,
                summary: value.summary,
                description: value.description,
                dueDate: value.dueDate,
                assigneeId: value.assignee,
                priority: value.priority,
                status: value.status
            })
            .subscribe({
                next: newTask => {
                    this.logging.success(`${value.summary} updated`);
                    this.taskUpdated.emit(newTask);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating task: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    refreshComments() {
        this.generalTasksDataService
            .findComments(this.generalTask.id)
            .subscribe(res => {
                this.comments = res as unknown as Comment[];
            });
    }

    onCreateComment(content: Delta) {
        this.generalTasksDataService
            .createComment(this.generalTask.id, content)
            .subscribe({
                next: () => {
                    this.logging.success(`Comment created`);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while creating comment: ${error.message}`
                    );
                }
            });
    }

    onUpdateComment($event: CommentUpdate) {
        this.generalTasksDataService
            .updateComment(
                this.generalTask.id,
                $event.commentId,
                $event.content
            )
            .subscribe({
                next: () => {
                    this.logging.success(`Comment updated`);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating comment: ${error.message}`
                    );
                }
            });
    }

    onDeleteComment($event: CommentDelete) {
        this.generalTasksDataService
            .deleteComment(this.generalTask.id, $event.commentId)
            .subscribe({
                next: () => {
                    this.logging.success(`Comment deleted`);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while deleting comment: ${error.message}`
                    );
                }
            });
    }
}
